import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProgressBar } from "@/components/ProgressBar";
import { PROGRAM, getProgram, getActiveProgramId, dayCompletion, itemId, phaseMasteryCount, isPhaseUnlocked, isProgramVisible } from "@/data/program";
import { ChevronRight, Target, Lock } from "lucide-react";

function phasePct(phase, completed) {
  let total = 0;
  let done = 0;
  phase.weeks.forEach((w) =>
    w.days.forEach((d) => {
      if (d.type !== "training") return;
      const c = dayCompletion(d, completed);
      total += c.total;
      done += c.done;
    })
  );
  return total ? Math.round((done / total) * 100) : 0;
}

export default function Program() {
  const { user, completed } = useAuth();
  const navigate = useNavigate();
  const program = getProgram(getActiveProgramId()) || PROGRAM;

  useEffect(() => {
    if (program && !isProgramVisible(program, user?.email)) {
      navigate("/select", { replace: true });
    }
  }, [program, user, navigate]);

  return (
    <div className="px-5 pt-12 animate-slideup">
      <h1 className="font-head text-4xl font-black uppercase tracking-tight text-white">
        Programma
      </h1>
      <p className="text-sm text-zinc-500 mt-1">{program.name}</p>

      <div className="mt-6 space-y-3">
        {program.phases.map((phase, idx) => {
          const isMastery = program.progressionType === "mastery";
          const unlocked = isPhaseUnlocked(program, idx, completed);

          // Locked mastery phase — disabled, grey, prompt to finish previous
          if (isMastery && !unlocked) {
            const prev = program.phases[idx - 1];
            return (
              <div
                key={phase.id}
                data-testid={`phase-card-${phase.id}`}
                aria-disabled="true"
                className="w-full text-left border border-zinc-900 bg-[#0d0d0d] opacity-70"
              >
                <div className="flex items-center justify-between p-5">
                  <div className="min-w-0">
                    <span className="font-head text-[11px] uppercase tracking-widest text-zinc-600">
                      {phase.tag}
                    </span>
                    <h2 className="font-head text-3xl font-bold uppercase tracking-tight text-zinc-500">
                      {phase.name}
                    </h2>
                    <p className="flex items-center gap-1.5 text-xs text-zinc-600 mt-1">
                      <Lock size={12} /> Completa lo {prev.name}
                    </p>
                  </div>
                  <Lock size={24} className="text-zinc-700 shrink-0" />
                </div>
              </div>
            );
          }

          // Unlocked mastery phase — show mastered-movements counter
          if (isMastery) {
            const { done, total } = phaseMasteryCount(phase, completed);
            const pct = total ? Math.round((done / total) * 100) : 0;
            return (
              <button
                key={phase.id}
                data-testid={`phase-card-${phase.id}`}
                onClick={() => navigate(`/program/${phase.id}`)}
                className="w-full text-left border border-zinc-800 bg-[#141414] active:scale-[0.99] transition-transform"
              >
                <div className="flex items-center justify-between p-5">
                  <div className="min-w-0">
                    <span className="font-head text-[11px] uppercase tracking-widest text-[#fa003f]">
                      {phase.tag}
                    </span>
                    <h2 className="font-head text-3xl font-bold uppercase tracking-tight text-white">
                      {phase.name}
                    </h2>
                    <p className="text-xs text-zinc-500">{phase.subtitle}</p>
                  </div>
                  <ChevronRight size={26} className="text-zinc-600 shrink-0" />
                </div>
                <div className="px-5 pb-4">
                  <div className="flex justify-between mb-1.5">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-500">
                      {done}/{total} movimenti padroneggiati
                    </span>
                    <span className="font-head text-sm text-white">{pct}%</span>
                  </div>
                  <ProgressBar pct={pct} />
                </div>
              </button>
            );
          }

          // Non-mastery (Cali Move) — unchanged
          const pct = phasePct(phase, completed);
          return (
            <button
              key={phase.id}
              data-testid={`phase-card-${phase.id}`}
              onClick={() => navigate(`/program/${phase.id}`)}
              className="w-full text-left border border-zinc-800 bg-[#141414] active:scale-[0.99] transition-transform"
            >
              <div className="flex items-center justify-between p-5">
                <div className="min-w-0">
                  <span className="font-head text-[11px] uppercase tracking-widest text-[#fa003f]">
                    {phase.tag}
                  </span>
                  <h2 className="font-head text-3xl font-bold uppercase tracking-tight text-white">
                    {phase.name}
                  </h2>
                  <p className="text-xs text-zinc-500">{phase.subtitle}</p>
                </div>
                <ChevronRight size={26} className="text-zinc-600 shrink-0" />
              </div>
              <div className="px-5 pb-4">
                <div className="flex justify-between mb-1.5">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500">
                    Progresso
                  </span>
                  <span className="font-head text-sm text-white">{pct}%</span>
                </div>
                <ProgressBar pct={pct} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Bonus goals */}
      {program.bonus && program.bonus.length > 0 && (
        <>
          <h3 className="mt-8 flex items-center gap-2 font-head text-xl uppercase tracking-wide text-zinc-400">
            <Target size={18} className="text-[#00f0ff]" /> Obiettivi extra
          </h3>
          <div className="mt-3 space-y-3">
            {program.bonus.map((b) => {
              const total = b.exercises.length;
              const done = b.exercises.filter((_, i) => completed.has(itemId(b.id, i))).length;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <button
                  key={b.id}
                  data-testid={`bonus-card-${b.id}`}
                  onClick={() => navigate(`/session/${b.id}`)}
                  className="w-full text-left border border-zinc-800 bg-[#141414] active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-center justify-between p-5">
                    <div className="min-w-0">
                      <h2 className="font-head text-2xl font-bold uppercase tracking-tight text-white">
                        {b.name}
                      </h2>
                      <p className="text-xs text-zinc-500">{b.desc}</p>
                    </div>
                    <ChevronRight size={24} className="text-zinc-600 shrink-0" />
                  </div>
                  <div className="px-5 pb-4">
                    <ProgressBar pct={pct} />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
